import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import MasterLayout from "../../masterLayout/MasterLayout";
import Breadcrumb from "../../components/Breadcrumb";
import { API_URL } from '../../config/api';
import CurrencyFormatter from '../../utils/CurrencyFormatter';

const NotazzIntegrationPage = () => {
  const navigate = useNavigate();

  // Config
  const [config, setConfig] = useState(null);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);
  const [editingApiKey, setEditingApiKey] = useState(false);

  // Regras
  const [regras, setRegras] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [planosProduto, setPlanosProduto] = useState([]);
  const [showRegraModal, setShowRegraModal] = useState(false);
  const [editingRegraId, setEditingRegraId] = useState(null);
  const [loadingPlanos, setLoadingPlanos] = useState(false);
  const [regraForm, setRegraForm] = useState({
    produto_id: '',
    plano_id: '',
    tipo_nota: '',
    momento_emissao: '',
    descricao_servico: ''
  });

  // Delete
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingRegraId, setDeletingRegraId] = useState(null);
  const [deletingRegraName, setDeletingRegraName] = useState('');

  // Historico
  const [notas, setNotas] = useState([]);
  const [notasPagination, setNotasPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [loadingNotas, setLoadingNotas] = useState(false);

  // Stats
  const [stats, setStats] = useState(null);

  // Feedback
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({ show: false, type: 'success', message: '' });

  const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  useEffect(() => {
    loadConfig();
    loadRegras();
    loadProdutos();
    loadStats();
    loadNotas(1);
  }, []);

  // ===================== LOADERS =====================

  const loadConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notazz/config`, { headers });
      if (response.ok) {
        const result = await response.json();
        setConfig(result.data);
      }
    } catch (error) {
      console.error('Erro ao carregar config:', error);
    }
  };

  const loadRegras = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notazz/regras`, { headers });
      if (response.ok) {
        const result = await response.json();
        setRegras(result.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar regras:', error);
    }
  };

  const loadProdutos = async () => {
    try {
      const response = await fetch(`${API_URL}/api/products`, { headers });
      if (response.ok) {
        const result = await response.json();
        setProdutos(result.data || result || []);
      }
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notazz/stats`, { headers });
      if (response.ok) {
        const result = await response.json();
        setStats(result.stats);
      }
    } catch (error) {
      console.error('Erro ao carregar stats:', error);
    }
  };

  const loadNotas = async (page = 1) => {
    try {
      setLoadingNotas(true);
      const response = await fetch(`${API_URL}/api/notazz/notas?page=${page}&limit=10`, { headers });
      if (response.ok) {
        const result = await response.json();
        setNotas(result.data || []);
        setNotasPagination(result.pagination || { total: 0, page: 1, pages: 1 });
      }
    } catch (error) {
      console.error('Erro ao carregar notas:', error);
    } finally {
      setLoadingNotas(false);
    }
  };

  const loadPlanosProduto = async (produtoId) => {
    if (!produtoId) {
      setPlanosProduto([]);
      return;
    }
    try {
      setLoadingPlanos(true);
      const response = await fetch(`${API_URL}/api/products/${produtoId}/plans`, { headers });
      if (response.ok) {
        const result = await response.json();
        setPlanosProduto(Array.isArray(result.data) ? result.data : []);
      }
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
    } finally {
      setLoadingPlanos(false);
    }
  };

  // ===================== CONFIG HANDLERS =====================

  const handleSaveConfig = async () => {
    if (!apiKeyInput.trim()) {
      setFeedback({ show: true, type: 'danger', message: 'Informe a API Key do Notazz' });
      return;
    }

    try {
      setSavingConfig(true);
      const response = await fetch(`${API_URL}/api/notazz/config`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ apiKey: apiKeyInput.trim() })
      });

      const text = await response.text();
      let result;
      try { result = JSON.parse(text); } catch { result = { success: false, message: text }; }

      if (result.success) {
        setConfig(result.data);
        setApiKeyInput('');
        setFeedback({ show: true, type: 'success', message: 'API Key configurada com sucesso!' });
        loadStats();
      } else {
        setFeedback({ show: true, type: 'danger', message: result.message || 'Erro ao salvar API Key' });
      }
    } catch (error) {
      setFeedback({ show: true, type: 'danger', message: 'Erro de conexao com o servidor' });
    } finally {
      setSavingConfig(false);
    }
  };

  const handleDisableConfig = async () => {
    try {
      await fetch(`${API_URL}/api/notazz/config`, { method: 'DELETE', headers });
      setConfig(null);
      setFeedback({ show: true, type: 'info', message: 'Integracao Notazz desativada' });
    } catch (error) {
      setFeedback({ show: true, type: 'danger', message: 'Erro ao desativar integracao' });
    }
  };

  // ===================== REGRA HANDLERS =====================

  const handleOpenRegraModal = () => {
    setEditingRegraId(null);
    setRegraForm({
      produto_id: '',
      plano_id: '',
      tipo_nota: 'nfe',
      momento_emissao: 'Pagamento aprovado',
      descricao_servico: ''
    });
    setPlanosProduto([]);
    setFeedback({ show: false, type: 'success', message: '' });
    setShowRegraModal(true);
  };

  const handleEditRegra = (regra) => {
    setEditingRegraId(regra.id);
    setRegraForm({
      produto_id: regra.produto_id,
      plano_id: regra.plano_id || '',
      tipo_nota: regra.tipo_nota,
      momento_emissao: regra.momento_emissao,
      descricao_servico: regra.descricao_servico || ''
    });
    loadPlanosProduto(regra.produto_id);
    setFeedback({ show: false, type: 'success', message: '' });
    setShowRegraModal(true);
  };

  const handleSaveRegra = async () => {
    const { produto_id, tipo_nota, momento_emissao, descricao_servico } = regraForm;

    if (!produto_id || !tipo_nota || !momento_emissao) {
      setFeedback({ show: true, type: 'danger', message: 'Preencha todos os campos obrigatorios' });
      return;
    }

    if (tipo_nota === 'nfse' && !descricao_servico.trim()) {
      setFeedback({ show: true, type: 'danger', message: 'Descricao do servico e obrigatoria para NFS-e' });
      return;
    }

    try {
      setLoading(true);
      const isEditing = !!editingRegraId;
      const url = isEditing
        ? `${API_URL}/api/notazz/regras/${editingRegraId}`
        : `${API_URL}/api/notazz/regras`;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(regraForm)
      });

      const text = await response.text();
      let result;
      try { result = JSON.parse(text); } catch { result = { success: false, message: text }; }

      if (result.success) {
        setShowRegraModal(false);
        setEditingRegraId(null);
        loadRegras();
        setFeedback({ show: true, type: 'success', message: isEditing ? 'Regra atualizada!' : 'Regra criada!' });
      } else {
        setFeedback({ show: true, type: 'danger', message: result.message || 'Erro ao salvar regra' });
      }
    } catch (error) {
      setFeedback({ show: true, type: 'danger', message: 'Erro de conexao com o servidor' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRegra = (regra) => {
    setDeletingRegraId(regra.id);
    setDeletingRegraName(regra.produto_nome || `Regra #${regra.id}`);
    setShowDeleteModal(true);
  };

  const confirmDeleteRegra = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/notazz/regras/${deletingRegraId}`, {
        method: 'DELETE',
        headers
      });

      const text = await response.text();
      let result;
      try { result = JSON.parse(text); } catch { result = { success: false, message: text }; }

      if (result.success) {
        setShowDeleteModal(false);
        setDeletingRegraId(null);
        loadRegras();
        setFeedback({ show: true, type: 'success', message: 'Regra removida com sucesso' });
      } else {
        setFeedback({ show: true, type: 'danger', message: result.message || 'Erro ao remover regra' });
      }
    } catch (error) {
      setFeedback({ show: true, type: 'danger', message: 'Erro de conexao com o servidor' });
    } finally {
      setLoading(false);
    }
  };

  // ===================== HELPERS =====================

  const getStatusBadge = (status) => {
    const map = {
      autorizada: { color: 'success', label: 'Autorizada' },
      processando: { color: 'info', label: 'Processando' },
      pendente: { color: 'warning', label: 'Pendente' },
      rejeitada: { color: 'danger', label: 'Rejeitada' },
      erro: { color: 'danger', label: 'Erro' },
      cancelada: { color: 'secondary', label: 'Cancelada' }
    };
    const s = map[status] || { color: 'secondary', label: status };
    return <span className={`badge bg-${s.color}`}>{s.label}</span>;
  };

  const getTipoNotaBadge = (tipo) => {
    if (tipo === 'nfe') return <span className="badge bg-primary">NF-e</span>;
    return <span className="badge bg-info">NFS-e</span>;
  };

  // ===================== RENDER =====================

  return (
    <MasterLayout>
      <Breadcrumb title="Notazz - Notas Fiscais" />

      <div className="container-fluid">
        {/* Titulo */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                  <Icon icon="mdi:file-document-check" width="32" className="text-primary" />
                </div>
                <div>
                  <span style={{ fontSize: '22px', fontWeight: '700', display: 'block' }}>
                    Notazz - Notas Fiscais
                  </span>
                  <span className="text-muted">
                    Emissao automatica de NF-e e NFS-e integrada ao fluxo de vendas
                  </span>
                </div>
              </div>
              <button
                className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                onClick={() => navigate('/wiki?section=notazz')}
              >
                <Icon icon="mdi:book-open-page-variant" width={16} />
                <span className="d-none d-md-inline">Documentacao</span>
              </button>
            </div>
          </div>
        </div>

        {/* Feedback global */}
        {feedback.show && (
          <Alert variant={feedback.type} dismissible onClose={() => setFeedback({ ...feedback, show: false })}>
            {feedback.message}
          </Alert>
        )}

        <div className="row g-4">
          {/* Coluna Esquerda - Config + Stats */}
          <div className="col-lg-7">
            {/* Card Config API Key */}
            <div className="card mb-4">
              <div className="card-header">
                <span style={{ fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center' }}>
                  <Icon icon="mdi:key" className="me-2" />
                  Configuracao da API Key
                </span>
              </div>
              <div className="card-body">
                {config ? (
                  <div>
                    <div className="d-flex align-items-center mb-3">
                      <span className={`badge bg-${config.isActive ? 'success' : 'warning'} me-2`}>
                        {config.isActive ? 'Ativa' : 'Inativa'}
                      </span>
                      <span className="text-muted">API Key: {config.apiKeyPreview}</span>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => {
                          setApiKeyInput(config.apiKeyPreview || '');
                          setEditingApiKey(true);
                        }}
                      >
                        <i className="fas fa-edit me-1"></i>
                        Alterar API Key
                      </button>
                      {config.isActive && (
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={handleDisableConfig}
                        >
                          <i className="fas fa-power-off me-1"></i>
                          Desativar
                        </button>
                      )}
                    </div>
                    {editingApiKey && (
                      <div className="mt-3 pt-3 border-top">
                        <span className="d-block text-muted mb-2" style={{ fontSize: '13px' }}>
                          Insira a nova API Key para substituir a atual:
                        </span>
                        <div className="input-group mb-2">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Cole a nova API Key aqui"
                            value={apiKeyInput}
                            onChange={(e) => setApiKeyInput(e.target.value)}
                          />
                        </div>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={async () => {
                              await handleSaveConfig();
                              setEditingApiKey(false);
                            }}
                            disabled={savingConfig}
                          >
                            {savingConfig ? (
                              <span className="spinner-border spinner-border-sm me-1"></span>
                            ) : (
                              <i className="fas fa-save me-1"></i>
                            )}
                            Salvar
                          </button>
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => {
                              setEditingApiKey(false);
                              setApiKeyInput('');
                            }}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <p className="text-muted mb-3">
                      Insira sua API Key do Notazz para ativar a emissao automatica de notas fiscais.
                      Voce encontra a chave no painel do Notazz em Configuracoes &gt; API.
                    </p>
                    <div className="input-group mb-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Cole sua API Key aqui"
                        value={apiKeyInput}
                        onChange={(e) => setApiKeyInput(e.target.value)}
                      />
                      <button
                        className="btn btn-primary"
                        onClick={handleSaveConfig}
                        disabled={savingConfig}
                      >
                        {savingConfig ? (
                          <span className="spinner-border spinner-border-sm me-1"></span>
                        ) : (
                          <i className="fas fa-save me-1"></i>
                        )}
                        Salvar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Card Stats */}
            {stats && stats.totalNotas > 0 && (
              <div className="card mb-4">
                <div className="card-header">
                  <span style={{ fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center' }}>
                    <Icon icon="mdi:chart-bar" className="me-2" />
                    Estatisticas
                  </span>
                </div>
                <div className="card-body">
                  <div className="row text-center">
                    <div className="col-4 col-md-2">
                      <div className="small text-muted">Total</div>
                      <div className="fw-bold fs-5">{stats.totalNotas}</div>
                    </div>
                    <div className="col-4 col-md-2">
                      <div className="small text-muted">Autorizadas</div>
                      <div className="fw-bold fs-5 text-success">{stats.autorizadas}</div>
                    </div>
                    <div className="col-4 col-md-2">
                      <div className="small text-muted">Pendentes</div>
                      <div className="fw-bold fs-5 text-warning">{stats.pendentes}</div>
                    </div>
                    <div className="col-4 col-md-2">
                      <div className="small text-muted">Erros</div>
                      <div className="fw-bold fs-5 text-danger">{stats.comErro}</div>
                    </div>
                    <div className="col-4 col-md-2">
                      <div className="small text-muted">NF-e</div>
                      <div className="fw-bold fs-5">{stats.totalNfe}</div>
                    </div>
                    <div className="col-4 col-md-2">
                      <div className="small text-muted">NFS-e</div>
                      <div className="fw-bold fs-5">{stats.totalNfse}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Card Historico de Notas */}
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <span style={{ fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center' }}>
                  <Icon icon="mdi:file-document-multiple" className="me-2" />
                  Historico de Notas
                </span>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => loadNotas(1)} disabled={loadingNotas}>
                  <i className="fas fa-sync-alt me-1"></i>
                  Atualizar
                </button>
              </div>
              <div className="card-body p-0">
                {loadingNotas ? (
                  <div className="text-center py-4">
                    <span className="spinner-border spinner-border-sm"></span>
                  </div>
                ) : notas.length === 0 ? (
                  <div className="text-center py-5">
                    <Icon icon="mdi:file-document-outline" width="48" className="text-muted mb-3" />
                    <p className="text-muted mb-0">Nenhuma nota emitida ainda</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead>
                          <tr>
                            <th>Tipo</th>
                            <th>Numero</th>
                            <th>Valor</th>
                            <th>Status</th>
                            <th>Data</th>
                            <th>Acoes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {notas.map((nota) => (
                            <tr key={nota.id}>
                              <td>{getTipoNotaBadge(nota.tipo_nota)}</td>
                              <td>{nota.numero_nota || '-'}</td>
                              <td>{CurrencyFormatter.centsToFormatted(nota.valor_total)}</td>
                              <td>{getStatusBadge(nota.status)}</td>
                              <td>{new Date(nota.created_at).toLocaleDateString('pt-BR')}</td>
                              <td>
                                <div className="d-flex gap-1">
                                  {nota.pdf_url && (
                                    <a href={nota.pdf_url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary" title="PDF">
                                      <i className="fas fa-file-pdf"></i>
                                    </a>
                                  )}
                                  {nota.xml_url && (
                                    <a href={nota.xml_url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-secondary" title="XML">
                                      <i className="fas fa-file-code"></i>
                                    </a>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Paginacao */}
                    {notasPagination.pages > 1 && (
                      <div className="d-flex justify-content-center py-3">
                        <nav>
                          <ul className="pagination pagination-sm mb-0">
                            {Array.from({ length: notasPagination.pages }, (_, i) => i + 1).map((page) => (
                              <li key={page} className={`page-item ${page === notasPagination.page ? 'active' : ''}`}>
                                <button className="page-link" onClick={() => loadNotas(page)}>
                                  {page}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </nav>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Coluna Direita - Regras */}
          <div className="col-lg-5">
            <div className="card h-100">
              <div className="card-header d-flex justify-content-between align-items-center">
                <span style={{ fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center' }}>
                  <Icon icon="mdi:clipboard-list" className="me-2" />
                  Regras de Emissao
                </span>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handleOpenRegraModal}
                  disabled={!config}
                  title={!config ? 'Configure a API Key primeiro' : ''}
                >
                  <i className="fas fa-plus me-1"></i>
                  Nova
                </button>
              </div>
              <div className="card-body p-0">
                {regras.length === 0 ? (
                  <div className="text-center py-5">
                    <Icon icon="mdi:clipboard-text-off-outline" width="48" className="text-muted mb-3" />
                    <p className="text-muted mb-1">Nenhuma regra configurada</p>
                    <p className="text-muted small">Crie regras para emitir notas automaticamente nas vendas</p>
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {regras.map((regra) => (
                      <div key={regra.id} className="list-group-item">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <span className="fw-semibold">{regra.produto_nome}</span>
                              {getTipoNotaBadge(regra.tipo_nota)}
                            </div>
                            {regra.plano_nome && (
                              <div className="small text-muted">
                                <i className="fas fa-tag me-1"></i>
                                Plano: {regra.plano_nome}
                              </div>
                            )}
                            <div className="small text-muted">
                              <i className="fas fa-clock me-1"></i>
                              {regra.momento_emissao}
                            </div>
                            {regra.descricao_servico && (
                              <div className="small text-muted text-truncate" style={{ maxWidth: '300px' }}>
                                <i className="fas fa-align-left me-1"></i>
                                {regra.descricao_servico}
                              </div>
                            )}
                          </div>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="p-0 d-flex align-items-center justify-content-center"
                              style={{ width: '26px', height: '26px' }}
                              onClick={() => handleEditRegra(regra)}
                              title="Editar regra"
                            >
                              <Icon icon="mdi:pencil" style={{ fontSize: '14px' }} />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="p-0 d-flex align-items-center justify-content-center"
                              style={{ width: '26px', height: '26px' }}
                              onClick={() => handleDeleteRegra(regra)}
                              title="Excluir regra"
                            >
                              <Icon icon="mdi:delete" style={{ fontSize: '14px' }} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Blocos explicativos */}
        <div className="row g-4 mt-2">
          <div className="col-md-4">
            <div className="card h-100 border-0 shadow-sm">
              <div className="card-body text-center">
                <div className="bg-primary bg-opacity-10 rounded-circle p-3 mx-auto mb-3" style={{ width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon icon="mdi:key-variant" width="28" className="text-primary" />
                </div>
                <span style={{ fontSize: '15px', fontWeight: '600', display: 'block', marginBottom: '8px' }}>1. Configure sua API Key</span>
                <p className="text-muted small mb-0">
                  Acesse o painel do Notazz, copie sua API Key em Configuracoes &gt; API e cole no campo acima. A chave sera validada automaticamente.
                </p>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card h-100 border-0 shadow-sm">
              <div className="card-body text-center">
                <div className="bg-success bg-opacity-10 rounded-circle p-3 mx-auto mb-3" style={{ width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon icon="mdi:clipboard-list-outline" width="28" className="text-success" />
                </div>
                <span style={{ fontSize: '15px', fontWeight: '600', display: 'block', marginBottom: '8px' }}>2. Crie regras de emissao</span>
                <p className="text-muted small mb-0">
                  Defina para cada produto/plano se deve emitir NF-e (produto) ou NFS-e (servico) e em qual momento: no pagamento aprovado ou agendado.
                </p>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card h-100 border-0 shadow-sm">
              <div className="card-body text-center">
                <div className="bg-info bg-opacity-10 rounded-circle p-3 mx-auto mb-3" style={{ width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon icon="mdi:send-check" width="28" className="text-info" />
                </div>
                <span style={{ fontSize: '15px', fontWeight: '600', display: 'block', marginBottom: '8px' }}>3. Emissao automatica</span>
                <p className="text-muted small mb-0">
                  Quando uma venda for confirmada, a nota fiscal e emitida automaticamente via Notazz e o comprador recebe o PDF por email.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Nova/Editar Regra */}
      <Modal show={showRegraModal} onHide={() => { setShowRegraModal(false); setEditingRegraId(null); }} size="lg">
        <Modal.Header closeButton>
          <span style={{ fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center' }}>
            <Icon icon={editingRegraId ? 'mdi:pencil' : 'mdi:plus-circle'} className="me-2" />
            {editingRegraId ? 'Editar Regra' : 'Nova Regra de Emissao'}
          </span>
        </Modal.Header>
        <Modal.Body>
          {feedback.show && feedback.type === 'danger' && (
            <Alert variant="danger" dismissible onClose={() => setFeedback({ ...feedback, show: false })}>
              {feedback.message}
            </Alert>
          )}

          <div className="mb-3">
            <Form.Label>Produto *</Form.Label>
            <Form.Select
              value={regraForm.produto_id}
              onChange={(e) => {
                const prodId = e.target.value;
                setRegraForm({ ...regraForm, produto_id: prodId, plano_id: '' });
                loadPlanosProduto(prodId);
              }}
            >
              <option value="">Selecione um produto</option>
              {produtos.map((prod) => (
                <option key={prod.id} value={prod.id}>
                  {prod.name} {prod.price ? `- ${CurrencyFormatter.centsToFormatted(prod.price)}` : ''}
                </option>
              ))}
            </Form.Select>
          </div>

          <div className="mb-3">
            <Form.Label>Plano (opcional)</Form.Label>
            <Form.Select
              value={regraForm.plano_id}
              onChange={(e) => setRegraForm({ ...regraForm, plano_id: e.target.value })}
              disabled={!regraForm.produto_id || loadingPlanos}
            >
              <option value="">Todos os planos</option>
              {planosProduto.map((plano) => (
                <option key={plano.id} value={plano.id}>
                  {plano.name} - {CurrencyFormatter.centsToFormatted(plano.price)}
                </option>
              ))}
            </Form.Select>
            {loadingPlanos && <small className="text-muted">Carregando planos...</small>}
          </div>

          <div className="mb-3">
            <Form.Label>Tipo de Nota *</Form.Label>
            <Form.Select
              value={regraForm.tipo_nota}
              onChange={(e) => setRegraForm({ ...regraForm, tipo_nota: e.target.value })}
            >
              <option value="">Selecione o tipo</option>
              <option value="nfe">NF-e (Nota Fiscal de Produto)</option>
              <option value="nfse">NFS-e (Nota Fiscal de Servico)</option>
            </Form.Select>
          </div>

          <div className="mb-3">
            <Form.Label>Momento da Emissao *</Form.Label>
            <Form.Select
              value={regraForm.momento_emissao}
              onChange={(e) => setRegraForm({ ...regraForm, momento_emissao: e.target.value })}
            >
              <option value="Pagamento aprovado">Pagamento aprovado</option>
            </Form.Select>
            <small className="text-muted">A nota sera emitida automaticamente quando o pagamento for confirmado.</small>
          </div>

          {regraForm.tipo_nota === 'nfse' && (
            <div className="mb-3">
              <Form.Label>Descricao do Servico *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Descreva o servico prestado para a NFS-e"
                value={regraForm.descricao_servico}
                onChange={(e) => setRegraForm({ ...regraForm, descricao_servico: e.target.value })}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowRegraModal(false); setEditingRegraId(null); }}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveRegra} disabled={loading}>
            {loading ? (
              <span className="spinner-border spinner-border-sm me-1"></span>
            ) : (
              <i className={`fas ${editingRegraId ? 'fa-save' : 'fa-plus'} me-1`}></i>
            )}
            {editingRegraId ? 'Salvar' : 'Criar Regra'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Confirmar Exclusao */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <span style={{ fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', color: '#dc3545' }}>
            <Icon icon="mdi:delete-alert" className="me-2" />
            Confirmar Exclusao
          </span>
        </Modal.Header>
        <Modal.Body>
          <p>Tem certeza que deseja remover a regra de emissao para <strong>{deletingRegraName}</strong>?</p>
          <p className="text-muted small mb-0">
            Notas ja emitidas nao serao afetadas. Novas vendas deste produto nao emitirao nota automaticamente.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={confirmDeleteRegra} disabled={loading}>
            {loading ? (
              <span className="spinner-border spinner-border-sm me-1"></span>
            ) : (
              <i className="fas fa-trash-alt me-1"></i>
            )}
            Remover
          </Button>
        </Modal.Footer>
      </Modal>
    </MasterLayout>
  );
};

export default NotazzIntegrationPage;
